"use client"

import { useState } from "react"
import type { Hero, HeroSkill } from "@/types/hero.types"
import { getSkillLevelStats, getSkillCooldownProgress, canUseSkill } from "@/systems/skill-system"

interface MobileSkillsPanelProps {
  isOpen: boolean
  onClose: () => void
  hero: Hero
  onUseSkill?: (skillId: string) => void
  onMobileSkillActivated?: () => void // Called when a targeted skill is activated
  pendingSkillId?: string | null
}

// Detailed skill view for drilldown
const SkillDetailView = ({ skill, hero, onBack }: { skill: HeroSkill; hero: Hero; onBack: () => void }) => {
  const stats = getSkillLevelStats(skill)
  const { canUse, reason } = canUseSkill(hero, skill.id)
  const isMaxLevel = skill.currentLevel >= skill.maxLevel
  const hasCooldown = stats.cooldown > 0
  const isPassive = skill.type === "passive"
  const hasCritStats = stats.critChance !== undefined

  return (
    <div className="p-4">
      {/* Back button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 mb-4 text-white/60 hover:text-white/80 transition-colors active:text-white"
      >
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M10 4L6 8L10 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        <span className="text-sm">Back</span>
      </button>

      {/* Skill icon and header */}
      <div className="flex items-start gap-4 mb-4">
        <div
          className="relative rounded-lg overflow-hidden flex-shrink-0"
          style={{
            width: 100,
            height: 100,
            backgroundColor: skill.iconColor,
            border: "3px solid #22d3ee",
          }}
        >
          {skill.icon && (
            <img
              src={skill.icon}
              alt={skill.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-amber-400 font-bold text-lg mb-1">{skill.name}</h3>
          <div className="flex items-center gap-2">
            <span className="text-amber-200/70 text-sm">Level {skill.currentLevel}</span>
            {isPassive && (
              <span className="text-cyan-400 text-xs font-semibold bg-cyan-400/20 px-2 py-0.5 rounded">
                Passive
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Description */}
      <p className="text-gray-300 text-sm leading-relaxed mb-4">{skill.description}</p>

      {/* Stats */}
      <div className="bg-black/30 rounded-lg p-3 space-y-2">
        <h4 className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-2">Current Stats</h4>
        {hasCritStats ? (
          <>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Crit Chance</span>
              <span className="text-cyan-400 font-semibold">
                {((stats.critChance || 0) * 100).toFixed(0)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Crit Damage</span>
              <span className="text-red-400 font-semibold">
                {stats.critMultiplier || 0}x
              </span>
            </div>
          </>
        ) : (
          <>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Damage</span>
              <span className="text-red-400 font-semibold">{stats.damage}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Mana Cost</span>
              <span className="text-blue-400 font-semibold">{stats.manaCost}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">Cooldown</span>
              <span className={hasCooldown ? "text-yellow-400 font-semibold" : "text-green-400 font-semibold"}>
                {hasCooldown ? `${(stats.cooldown / 60).toFixed(1)}s` : "None"}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Hotkey */}
      {!isPassive && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-gray-500 text-sm">Hotkey</span>
          <kbd className="bg-gray-800 text-amber-300 px-3 py-1.5 rounded text-sm font-mono">
            {skill.hotkey}
          </kbd>
        </div>
      )}

      {/* Status messages */}
      {!canUse && reason && !isPassive && (
        <div className="mt-3 text-red-400 text-sm bg-red-400/10 px-3 py-2 rounded">
          {reason}
        </div>
      )}
      {!isMaxLevel && (canUse || isPassive) && (
        <div className="mt-3 text-green-400/80 text-sm bg-green-400/10 px-3 py-2 rounded">
          Upgrade available at next level
        </div>
      )}
    </div>
  )
}

const SkillButton = ({
  skill,
  hero,
  onUseSkill,
  onClose,
  onMobileSkillActivated,
  isPending,
  onInfoClick,
}: {
  skill: HeroSkill
  hero: Hero
  onUseSkill?: (skillId: string) => void
  onClose: () => void
  onMobileSkillActivated?: () => void
  isPending: boolean
  onInfoClick: () => void
}) => {
  const { canUse } = canUseSkill(hero, skill.id)
  const cooldownProgress = getSkillCooldownProgress(skill)
  const isOnCooldown = cooldownProgress < 1
  const cooldownSeconds = skill.currentCooldown > 0 ? Math.ceil(skill.currentCooldown / 60) : 0
  const isPassive = skill.type === "passive"

  const handleClick = () => {
    if (!canUse || isPassive) return
    onUseSkill?.(skill.id)
    // Call the callback for non-passive skills (targeted skills will benefit from this)
    if (onMobileSkillActivated) {
      onMobileSkillActivated()
    }
    onClose()
  }

  return (
    <div className="relative">
      <button
        onClick={handleClick}
        disabled={!canUse || isPassive}
        className={`relative overflow-hidden transition-all rounded-lg ${
          isPending
            ? "ring-2 ring-cyan-400 ring-offset-2 ring-offset-slate-900 animate-pulse"
            : canUse && !isPassive
              ? "active:scale-95"
              : "opacity-60"
        }`}
        style={{
          width: 80,
          height: 80,
          backgroundColor: skill.iconColor,
          border: `3px solid ${isPending ? "#22d3ee" : canUse && !isPassive ? "#22d3ee" : "#6b7280"}`,
        }}
      >
        {/* Skill icon image */}
        {skill.icon && (
          <img
            src={skill.icon}
            alt={skill.name}
            className="absolute inset-0 w-full h-full object-cover rounded-md"
          />
        )}

        {/* Cooldown overlay */}
        {isOnCooldown && (
          <div
            className="absolute inset-0 bg-black/70 rounded-md"
            style={{
              clipPath: `polygon(0 ${(1 - cooldownProgress) * 100}%, 100% ${(1 - cooldownProgress) * 100}%, 100% 100%, 0 100%)`,
            }}
          />
        )}

        {/* Cooldown text */}
        {isOnCooldown && (
          <span
            className="absolute inset-0 flex items-center justify-center text-white font-bold text-2xl"
            style={{ textShadow: "0 0 4px black" }}
          >
            {cooldownSeconds}
          </span>
        )}

        {/* Level indicator - top left */}
        {!isOnCooldown && (
          <span className="absolute top-1 left-1 text-amber-400 font-bold text-sm bg-black/60 px-1.5 py-0.5 rounded">
            {skill.currentLevel}
          </span>
        )}

        {/* Hotkey indicator - bottom right, hide for passive skills */}
        {!isPassive && !isOnCooldown && (
          <span className="absolute bottom-1 right-1 text-white font-bold text-xs bg-black/60 px-1.5 py-0.5 rounded">
            {skill.hotkey}
          </span>
        )}

        {/* Passive indicator */}
        {isPassive && (
          <span className="absolute bottom-1 left-1/2 -translate-x-1/2 text-cyan-400 font-semibold text-[10px] bg-black/60 px-1.5 py-0.5 rounded">
            Passive
          </span>
        )}
      </button>

      {/* Info button - positioned outside the skill button */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onInfoClick()
        }}
        className="absolute -top-1 -right-1 w-6 h-6 bg-slate-700 hover:bg-slate-600 active:bg-slate-500 rounded-full flex items-center justify-center z-10 border border-white/20 transition-colors"
      >
        <span className="text-white/80 text-xs font-bold">i</span>
      </button>
    </div>
  )
}

export const MobileSkillsPanel = ({
  isOpen,
  onClose,
  hero,
  onUseSkill,
  onMobileSkillActivated,
  pendingSkillId,
}: MobileSkillsPanelProps) => {
  const [drilldownSkillId, setDrilldownSkillId] = useState<string | null>(null)

  if (!isOpen) return null

  const skills = hero.skills || []
  const drilldownSkill = drilldownSkillId ? skills.find((s) => s.id === drilldownSkillId) : null

  // Handle closing - reset drilldown state
  const handleClose = () => {
    setDrilldownSkillId(null)
    onClose()
  }

  return (
    <>
      {/* Backdrop overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-[60]"
        onClick={handleClose}
      />

      {/* Bottom sheet container */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[70] animate-slide-up"
        style={{
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div className="bg-gradient-to-b from-slate-800 to-slate-900 rounded-t-2xl border-t border-x border-amber-500/40 shadow-lg shadow-black/50">
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-12 h-1 rounded-full bg-white/30" />
          </div>

          {drilldownSkill ? (
            // Drilldown view - no header, just content with back button
            <SkillDetailView
              skill={drilldownSkill}
              hero={hero}
              onBack={() => setDrilldownSkillId(null)}
            />
          ) : (
            <>
              {/* Header with title and close button */}
              <div className="flex items-center justify-center relative px-4 pb-3 border-b border-white/10">
                <span className="text-amber-200 font-bold text-base">Skills</span>
                <button
                  onClick={handleClose}
                  className="absolute right-4 p-2 rounded-lg hover:bg-white/10 transition-colors active:bg-white/20"
                >
                  <svg width="20" height="20" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M4 4L12 12M12 4L4 12" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
              </div>

              {/* Skills grid - 2x2 layout with skill names below */}
              <div className="p-4">
                <div className="grid grid-cols-2 gap-4 justify-items-center">
                  {[0, 1, 2, 3].map((i) => {
                    const skill = skills[i]
                    if (!skill) {
                      return (
                        <div
                          key={i}
                          className="w-20 h-20 bg-gray-800/60 rounded-lg border-2 border-gray-600"
                        />
                      )
                    }

                    const isPending = pendingSkillId === skill.id

                    return (
                      <div key={i} className="flex flex-col items-center gap-1">
                        <SkillButton
                          skill={skill}
                          hero={hero}
                          onUseSkill={onUseSkill}
                          onClose={handleClose}
                          onMobileSkillActivated={onMobileSkillActivated}
                          isPending={isPending}
                          onInfoClick={() => setDrilldownSkillId(skill.id)}
                        />
                        <span className="text-white/80 text-xs font-medium text-center max-w-[100px] leading-tight">
                          {skill.name}
                        </span>
                      </div>
                    )
                  })}
                </div>

                {/* Hint text */}
                <p className="text-center text-white/40 text-xs mt-4">
                  Tap a skill to use it
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  )
}
